import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  fetchOrderLanguage,
  fetchOrderSubstrates,
} from "../../../Services/apiService";
import { calculateGasRise } from "./utils/gasCalculation";
import { getTranslations } from "./utils/translations";
import { REPORT_FONT, ensureReportFontsLoaded } from "../../../utils/pdfFont";
import "./reportFonts.css";

const REPORT_LOGO_SRC = "/reportlogo.png";
const REPORT_LOGO_ASPECT = 3; // reportlogo.png is 3000×1000
const LOGO_HEIGHT_PX = 36;
const LOGO_WIDTH_PX = LOGO_HEIGHT_PX * REPORT_LOGO_ASPECT;

// Layout matches page box (210×297mm, MUI p:4 = 32px padding)
const PAGE_PAD_MM = 8.5;
const LOGO_HEIGHT_MM = 9.5;
const LOGO_WIDTH_MM = LOGO_HEIGHT_MM * REPORT_LOGO_ASPECT;

const PDF_CAPTURE_SCALE = 2;
const PDF_JPEG_QUALITY = 0.82;
// ~480px PNG keeps transparency + small size; full 3000px embed was ~13MB
const LOGO_PDF_MAX_WIDTH = 480;

let cachedReportLogoDataUrl = null;

const loadReportLogoDataUrl = () => {
  if (cachedReportLogoDataUrl) {
    return Promise.resolve(cachedReportLogoDataUrl);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, LOGO_PDF_MAX_WIDTH / img.naturalWidth);
      const width = Math.round(img.naturalWidth * scale);
      const height = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      cachedReportLogoDataUrl = canvas.toDataURL("image/png");
      resolve(cachedReportLogoDataUrl);
    };
    img.onerror = () => reject(new Error("Failed to load report logo"));
    img.src = REPORT_LOGO_SRC;
  });
};

const addSharpLogoToPdf = (pdf, pageWidth) => {
  if (!cachedReportLogoDataUrl) return;
  const x = pageWidth - PAGE_PAD_MM - LOGO_WIDTH_MM;
  const y = PAGE_PAD_MM;
  pdf.addImage(
    cachedReportLogoDataUrl,
    "PNG",
    x,
    y,
    LOGO_WIDTH_MM,
    LOGO_HEIGHT_MM
  );
};

const html2canvasOptions = (element) => ({
  scale: PDF_CAPTURE_SCALE,
  backgroundColor: "#f4f6f9",
  useCORS: true,
  allowTaint: true,
  logging: false,
  imageTimeout: 0,
  onclone: (clonedDoc) => {
    const style = clonedDoc.createElement("style");
    style.textContent = `
      .report-pdf-root, .report-pdf-root * {
        font-family: ${REPORT_FONT} !important;
      }
      .report-pdf-logo {
        visibility: hidden !important;
      }
    `;
    clonedDoc.head.appendChild(style);
    const clonedRoot = clonedDoc.querySelector(".report-pdf-root");
    if (clonedRoot) {
      clonedRoot.style.fontFamily = REPORT_FONT;
    }
  },
});

/**
 * ReportContent — shared forwardRef component.
 *
 * Props:
 *   reportData       — full report data object
 *   orderId          — order identifier
 *   testId           — test identifier
 *   onImagesLoaded   — optional callback(boolean) fired when image-load state changes
 *
 * Exposed via ref (useImperativeHandle):
 *   handleGenerateReport()  — downloads the PDF
 *   generatePdfBlob()       — returns a Promise<Blob> of the PDF (for sending)
 */
const ReportContent = forwardRef(
  ({ reportData, orderId, testId, onImagesLoaded }, ref) => {
    const [orderLanguage, setOrderLanguage] = useState(null);
    const [languageLoading, setLanguageLoading] = useState(true);
    const [substrate, setSubstrate] = useState("");

    const page1Ref = useRef(null);
    const page2Ref = useRef(null);

    // ── Mobile zoom ─────────────────────────────────────────────────────────
    // page1Ref/page2Ref always keep their fixed 210mm×297mm size so
    // html2canvas captures them unscaled (see buildPdf below) — `zoom` only
    // scales a non-captured ancestor wrapper for on-screen viewing.
    const PAGE_WIDTH_PX = 793.7; // 210mm @ 96dpi
    const PAGE_HEIGHT_PX = 1122.5; // 297mm @ 96dpi
    const REPORT_ZOOM_MARGIN_PX = 32;
    const MIN_ZOOM_FACTOR = 0.5;
    const MAX_ZOOM_FACTOR = 1.5;
    const ZOOM_STEP = 0.1;

    const [zoom, setZoom] = useState(1);
    const [fitScale, setFitScale] = useState(1);
    const manualZoomRef = useRef(false);

    useEffect(() => {
      const computeFitScale = () => {
        const scale = Math.min(
          1,
          (window.innerWidth - REPORT_ZOOM_MARGIN_PX) / PAGE_WIDTH_PX
        );
        setFitScale(scale);
        if (!manualZoomRef.current) setZoom(scale);
      };
      computeFitScale();
      window.addEventListener("resize", computeFitScale);
      return () => window.removeEventListener("resize", computeFitScale);
    }, []);

    const handleZoomIn = () => {
      manualZoomRef.current = true;
      setZoom((z) =>
        Math.min(fitScale * MAX_ZOOM_FACTOR, Math.round((z + ZOOM_STEP) * 100) / 100)
      );
    };
    const handleZoomOut = () => {
      manualZoomRef.current = true;
      setZoom((z) =>
        Math.max(fitScale * MIN_ZOOM_FACTOR, Math.round((z - ZOOM_STEP) * 100) / 100)
      );
    };
    const handleZoomReset = () => {
      manualZoomRef.current = false;
      setZoom(fitScale);
    };

    // ── Image loading ──────────────────────────────────────────────────────────
    useEffect(() => {
      const checkImagesLoaded = () => {
        const images = document.querySelectorAll("img");
        const allLoaded = Array.from(images).every((img) => img.complete);
        onImagesLoaded?.(allLoaded);
      };

      checkImagesLoaded();

      const images = document.querySelectorAll("img");
      images.forEach((img) => {
        img.addEventListener("load", checkImagesLoaded);
        img.addEventListener("error", checkImagesLoaded);
      });

      return () => {
        images.forEach((img) => {
          img.removeEventListener("load", checkImagesLoaded);
          img.removeEventListener("error", checkImagesLoaded);
        });
      };
    }, [onImagesLoaded]);

    // ── Language normalisation ─────────────────────────────────────────────────
    const normalizeLanguage = (value) => {
      if (!value || typeof value !== "string") return "english";
      const lower = value.toLowerCase().trim();

      if (
        lower.includes("france") ||
        lower.includes("french") ||
        lower === "fr"
      )
        return "french";

      if (
        lower.includes("germany") ||
        lower.includes("german") ||
        lower.includes("deutsch") ||
        lower === "de" ||
        lower.includes("austria") ||
        lower.includes("austrian") ||
        lower === "at"
      )
        return "german";

      if (
        lower.includes("netherlands") ||
        lower.includes("dutch") ||
        lower === "nl"
      )
        return "dutch";

      return "english";
    };

    // ── Fetch language ─────────────────────────────────────────────────────────
    useEffect(() => {
      const getOrderLanguage = async () => {
        if (orderId) {
          try {
            console.log("🌐 Fetching language for order:", orderId);
            const languageData = await fetchOrderLanguage(orderId);
            const languageValue =
              languageData?.order_language ||
              languageData?.language ||
              languageData?.data?.order_language ||
              languageData?.data?.language ||
              languageData;
            const normalized = normalizeLanguage(languageValue);
            setOrderLanguage(normalized);
            console.log("✅ Order language fetched:", languageData);
            console.log("🗣️ Normalized report language:", normalized);
          } catch (error) {
            console.error("❌ Error fetching order language:", error);
            setOrderLanguage("english");
          } finally {
            setLanguageLoading(false);
          }
        } else {
          setLanguageLoading(false);
        }
      };

      getOrderLanguage();
    }, [orderId]);

    // ── Fetch substrate ────────────────────────────────────────────────────────
    useEffect(() => {
      const getSubstrate = async () => {
        if (orderId && testId) {
          try {
            console.log(
              "🧪 Fetching substrate for order:",
              orderId,
              "test:",
              testId
            );
            const result = await fetchOrderSubstrates(orderId);
            if (result?.success && result?.data) {
              const matched = result.data.find(
                (item) => item.test_identifier === testId
              );
              if (matched) {
                setSubstrate(matched.substrate);
                console.log("✅ Substrate fetched:", matched.substrate);
              } else {
                console.warn(
                  "⚠️ No matching test_identifier found for:",
                  testId
                );
              }
            }
          } catch (error) {
            console.error("❌ Error fetching substrate:", error);
          }
        }
      };

      getSubstrate();
    }, [orderId, testId]);

    // ── PDF helpers ────────────────────────────────────────────────────────────
    const waitForImages = (element) => {
      const images = element.querySelectorAll("img");
      const promises = Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });
      return Promise.all(promises);
    };

    const buildPdf = async () => {
      await ensureReportFontsLoaded();
      await loadReportLogoDataUrl();
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      if (page1Ref.current) {
        await waitForImages(page1Ref.current);
        const canvas1 = await html2canvas(
          page1Ref.current,
          html2canvasOptions(page1Ref.current)
        );
        const imgData1 = canvas1.toDataURL("image/jpeg", PDF_JPEG_QUALITY);
        pdf.addImage(imgData1, "JPEG", 0, 0, pageWidth, pageHeight);
        addSharpLogoToPdf(pdf, pageWidth);
      }

      if (page2Ref.current) {
        pdf.addPage();
        await waitForImages(page2Ref.current);
        const canvas2 = await html2canvas(
          page2Ref.current,
          html2canvasOptions(page2Ref.current)
        );
        const imgData2 = canvas2.toDataURL("image/jpeg", PDF_JPEG_QUALITY);
        pdf.addImage(imgData2, "JPEG", 0, 0, pageWidth, pageHeight);
        addSharpLogoToPdf(pdf, pageWidth);
      }

      return pdf;
    };

    const handleGenerateReport = async () => {
      const pdf = await buildPdf();
      pdf.save(`Sibolab-Report-${testId}.pdf`);
    };

    const generatePdfBlob = async () => {
      const pdf = await buildPdf();
      return new Blob([pdf.output("arraybuffer")], {
        type: "application/pdf",
      });
    };

    useImperativeHandle(ref, () => ({
      handleGenerateReport,
      generatePdfBlob,
    }));

    // ── Loading spinner ────────────────────────────────────────────────────────
    if (languageLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    // ── Derived values (computed after language is known) ──────────────────────
    const reportLanguage = orderLanguage || "english";
    console.log("🌐 Order Language:", orderLanguage);
    console.log("🗣️ Report Language:", reportLanguage);
    console.log("📝 Using translations for:", reportLanguage.toUpperCase());

    const t = getTranslations(reportLanguage);
    t.generalDetails = "";
    t.voluntaryDetails = "";

    const getSymptomTranslation = (symptomKey) =>
      t.symptoms?.[symptomKey] ||
      symptomKey.charAt(0).toUpperCase() +
        symptomKey.slice(1).replace(/([A-Z])/g, " $1");

    const getTestSymptomTranslation = (symptomKey) =>
      t.testSymptoms?.[symptomKey] ||
      symptomKey.charAt(0).toUpperCase() +
        symptomKey.slice(1).replace(/([A-Z])/g, " $1");

    const formatDateForDisplay = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      } catch (error) {
        console.error("Date formatting error:", error);
        return dateString;
      }
    };

    const timeIntervals = [0, 20, 40, 60, 80, 90, 100, 120, 150, 180];
    const h2Values = reportData?.analysisData?.h2_values || [];
    const ch4Values = reportData?.analysisData?.ch4_values || [];
    const co2Values = reportData?.analysisData?.co2_values || [];

    const chartData = timeIntervals.map((time, index) => ({
      time,
      H2:
        h2Values[index] === "-"
          ? "-"
          : isNaN(Number(h2Values[index] || 0))
            ? "-"
            : Number(h2Values[index] || 0),
      CH4:
        ch4Values[index] === "-"
          ? "-"
          : isNaN(Number(ch4Values[index] || 0))
            ? "-"
            : Number(ch4Values[index] || 0),
      CO2:
        co2Values[index] === "-"
          ? "-"
          : isNaN(Number(co2Values[index] || 0))
            ? "-"
            : Number(co2Values[index] || 0),
    }));

    const calculateYAxisTicks = () => {
      const allNumericValues = [];
      chartData.forEach((point) => {
        if (point.H2 !== "-" && !isNaN(point.H2))
          allNumericValues.push(Number(point.H2));
        if (point.CH4 !== "-" && !isNaN(point.CH4))
          allNumericValues.push(Number(point.CH4));
      });
      if (allNumericValues.length === 0)
        return [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200];
      const maxValue = Math.max(...allNumericValues);
      const roundedMax = Math.ceil(maxValue / 10) * 10;
      let interval = 10;
      if (roundedMax / interval > 15) interval = 20;
      if (roundedMax / interval > 15) interval = 30;
      if (roundedMax / interval > 15) interval = 50;
      if (roundedMax / interval > 15) interval = 100;
      const ticks = [];
      for (let i = 0; i <= roundedMax; i += interval) ticks.push(i);
      return ticks;
    };

    const yAxisTicks = calculateYAxisTicks();

    const getGasRiseLabel = (result) =>
      result.isNotConspicuous ? "0 ppm" : `${result.numericValue} ppm`;

    const getExplanationNumericValue = (result) =>
      result.isNotConspicuous ? 0 : result.numericValue;

    const cleanedSubstrate = substrate.replace(/\bSIBO\b/g, "").trim();

    let timeLimit = 180;
    if (
      substrate.toLowerCase().includes("lactulose") ||
      substrate.toLowerCase().includes("glucose")
    ) {
      timeLimit = 90;
    }

    const h2Result = calculateGasRise(
      reportData?.analysisData?.h2_values || [],
      timeLimit
    );
    const ch4Result = calculateGasRise(
      reportData?.analysisData?.ch4_values || [],
      timeLimit
    );

    const co2AllValues =
      reportData?.analysisData?.co2_values
        ?.filter((value) => value !== "-" && value !== "" && value != null)
        ?.map(Number) || [];
    // ── CO₂ status (Gas Build-up column, CO₂ row of the Summary table) ──────
    // TEMPORARILY DISABLED (customer request, Nov 2026).
    //
    // Previous logic (unchanged since the first report generator, Aug 2025):
    //   If ANY of the 10 CO₂ readings (all time points, not only the 90-min
    //   window) was below 1.5 %, the report showed "Too Low" (t.tooLow),
    //   otherwise "Normal" (t.normal).
    //   Problem: a single slightly low reading (e.g. 1.4 % at minute 0) while
    //   all other readings were normal (2.3–2.9 %) produced "Too Low".
    //
    // Current behaviour: always show the "normal" text, regardless of the
    // readings:
    //   - German  → "In Ordnung"
    //   - English → "Normal"
    //   - French  → "Normal"
    //
    // To restore the old behaviour, delete the line below and uncomment the
    // original block. `co2AllValues` is kept above for that purpose.
    //
    // const co2Status = co2AllValues.some((value) => value < 1.5)
    //   ? t.tooLow
    //   : t.normal;
    const co2Status = t.normal;

    let referenceValues = { H2: "-", CH4: "-" };
    if (substrate.includes("Lactulose")) referenceValues = { H2: "20", CH4: "10" };
    else if (substrate.includes("Glucose")) referenceValues = { H2: "12", CH4: "10" };
    else if (substrate.includes("Fructose")) referenceValues = { H2: "20", CH4: "10" };
    else if (substrate.includes("Lactose")) referenceValues = { H2: "20", CH4: "10" };

    const useFont = REPORT_FONT;
    const reportTitleSize = "34px";
    const innerHeadingSize = "14px";
    const innerTextSize = "12px";

    const hasMissingValues = () => {
      const h2 = reportData?.analysisData?.h2_values || [];
      const ch4 = reportData?.analysisData?.ch4_values || [];
      const co2 = reportData?.analysisData?.co2_values || [];
      return (
        h2.some((v) => v === "-" || v === "" || v == null) ||
        ch4.some((v) => v === "-" || v === "" || v == null) ||
        co2.some((v) => v === "-" || v === "" || v == null)
      );
    };

    const getMissingIntervals = () => {
      const h2 = reportData?.analysisData?.h2_values || [];
      const ch4 = reportData?.analysisData?.ch4_values || [];
      const co2 = reportData?.analysisData?.co2_values || [];
      const missing = [];
      timeIntervals.forEach((time, index) => {
        if (
          h2[index] === "-" ||
          h2[index] === "" ||
          h2[index] == null ||
          ch4[index] === "-" ||
          ch4[index] === "" ||
          ch4[index] == null ||
          co2[index] === "-" ||
          co2[index] === "" ||
          co2[index] == null
        ) {
          missing.push(time);
        }
      });
      return missing;
    };

    const getMissingValuesMessage = () => {
      const missing = getMissingIntervals();
      if (missing.length === 0) return null;
      let minutesText = "";
      if (missing.length === 1) minutesText = missing[0];
      else if (missing.length === 2)
        minutesText = `${missing[0]} ${t.and} ${missing[1]}`;
      else
        minutesText =
          missing.slice(0, -1).join(", ") +
          ` ${t.and} ${missing[missing.length - 1]}`;
      return `${t.missingDataFrist} ${minutesText} ${t.missingDataSecond}`;
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
      <Box
        sx={{
          marginTop: "20px",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          py: 4,
          overflow: "auto",
          maxWidth: "100%",
        }}
      >
        {/* ── PAGE 1 ─────────────────────────────────────────────────────────── */}
        {/* Scale wrapper: page1Ref keeps its fixed 210mm/297mm size (html2canvas
            captures it unscaled below); only this ancestor pair shrinks the
            on-screen preview to fit narrow viewports. */}
        <Box sx={{ width: PAGE_WIDTH_PX * zoom, height: PAGE_HEIGHT_PX * zoom }}>
        <Box
          sx={{
            width: PAGE_WIDTH_PX,
            height: PAGE_HEIGHT_PX,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
          }}
        >
        <Box
          ref={page1Ref}
          className="report-pdf-root"
          sx={{
            width: "210mm",
            height: "297mm",
            backgroundColor: "#f4f6f9",
            background: "#f4f6f9",
            boxShadow: 5,
            p: 4,
            border: "1px solid #e0e0e0",
            fontFamily: useFont,
          }}
        >
          {/* Header: title + logo */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: reportTitleSize,
                fontWeight: "bold",
                fontFamily: useFont,
                color: "#000000",
                lineHeight: 1.2,
                m: 0,
              }}
            >
              {t.title}
            </Typography>
            <img
              className="report-pdf-logo"
              src={REPORT_LOGO_SRC}
              alt="Logo"
              crossOrigin="anonymous"
              style={{
                height: `${LOGO_HEIGHT_PX}px`,
                width: `${LOGO_WIDTH_PX}px`,
                display: "block",
              }}
            />
          </Box>

          {/* Patient Information */}
          <Box sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#697e93",
                fontFamily: useFont,
              }}
            >
              {t.generalDetails}
            </Typography>

            {/* Two white boxes in a row */}
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              {/* Patient details box */}
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    fontWeight: "bold",
                    color: "#000000",
                    fontFamily: useFont,
                    mb: 1,
                  }}
                >
                  {t.patientDetails}
                </Typography>

                <Box>
                  {/* Last name */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.lastName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {reportData?.formData?.mandatory_information?.last_name ||
                        "N/A"}
                    </Typography>
                  </Box>

                  {/* First name */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.firstName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {reportData?.formData?.mandatory_information
                        ?.first_name || "N/A"}
                    </Typography>
                  </Box>

                  {/* Date of birth */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.dateOfBirth}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {formatDateForDisplay(
                        reportData?.formData?.mandatory_information?.date_of_birth
                      )}
                    </Typography>
                  </Box>

                  {/* Report No */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.reportNo}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {reportData?.formData?.test_information
                        ?.test_identifier || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Test details box */}
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    fontWeight: "bold",
                    color: "#000000",
                    fontFamily: useFont,
                    mb: 1,
                  }}
                >
                  {t.testDetails}
                </Typography>

                <Box>
                  {/* Date of sample collection */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.dateOfSampleCollection}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {formatDateForDisplay(
                        reportData?.additionalInfo?.date_of_sampling
                      )}
                    </Typography>
                  </Box>

                  {/* Date of analysis */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.dateOfAnalysis}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {formatDateForDisplay(
                        reportData?.additionalInfo?.date_evaluation
                      )}
                    </Typography>
                  </Box>

                  {/* Substrate used */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.substratUsed}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {cleanedSubstrate || "N/A"}
                    </Typography>
                  </Box>

                  {/* Report sent to */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontWeight: "bold",
                        fontFamily: useFont,
                        color: "#697e93",
                      }}
                    >
                      {t.reportSentTo}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: innerTextSize,
                        fontFamily: useFont,
                        textAlign: "right",
                      }}
                    >
                      {reportData?.formData?.therapist?.other ||
                      reportData?.formData?.therapist?.therapist_name === ""
                        ? t.patient
                        : `${t.patient} and ${t.therapist}`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Typography
              sx={{
                mt: 0.5,
                mb: 0.5,
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#697e93",
                fontFamily: useFont,
              }}
            >
              {t.voluntaryDetails}
            </Typography>

            {/* 4-box grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                width: "100%",
              }}
            >
              {/* Box 1 — Physician or Therapist (R1 C1) */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  height: "auto",
                  minHeight: "60px",
                  gridColumn: "1",
                  gridRow: "1",
                }}
              >
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    fontWeight: "bold",
                    fontFamily: useFont,
                    color: "#697e93",
                  }}
                >
                  {t.physicianOrTherapist}
                </Typography>
                <Typography
                  sx={{
                    ml: 2,
                    fontSize: innerTextSize,
                    fontFamily: useFont,
                    color: "#000000",
                  }}
                >
                  {reportData?.formData?.therapist?.therapist_name || "N/A"}
                </Typography>
              </Box>

              {/* Box 2 — Occurrence of Symptoms (R1-R2 C2) */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  height: "auto",
                  minHeight: "100px",
                  maxHeight: "200px",
                  gridColumn: "2",
                  gridRow: "1 / span 2",
                }}
              >
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    fontWeight: "bold",
                    fontFamily: useFont,
                    color: "#697e93",
                  }}
                >
                  {t.occurenceOfSymptoms}
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: innerTextSize,
                    fontFamily: useFont,
                    color: "#000000",
                  }}
                >
                  {(() => {
                    const symptoms =
                      reportData?.formData?.testSymptoms || {};
                    const symptomTexts = [];
                    Object.entries(symptoms).forEach(([key, value]) => {
                      if (key !== "others" && value.checked) {
                        symptomTexts.push(
                          `${getTestSymptomTranslation(key)} (${t.startingAt} ${value.minutes} ${t.minutes})`
                        );
                      }
                    });
                    if (
                      symptoms.others?.checked &&
                      symptoms.others.symptoms?.length > 0
                    ) {
                      symptoms.others.symptoms.forEach((sym) => {
                        if (sym.description && sym.minutes) {
                          symptomTexts.push(
                            `${sym.description} (${t.startingAt} ${sym.minutes} ${t.minutes})`
                          );
                        }
                      });
                    }
                    return symptomTexts.length > 0
                      ? `${t.symptomsReported} ${symptomTexts.join(", ")} .`
                      : t.noSymptoms;
                  })()}
                </Typography>
              </Box>

              {/* Box 3 — Daily Symptoms (R2-R3 C1) */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  height: "auto",
                  minHeight: "100px",
                  maxHeight: "200px",
                  gridColumn: "1",
                  gridRow: "2 / span 2",
                }}
              >
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    fontWeight: "bold",
                    fontFamily: useFont,
                    color: "#697e93",
                  }}
                >
                  {t.dailySymptoms}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateRows: "repeat(2, 1fr)",
                    gridAutoFlow: "column",
                    gridAutoColumns: "minmax(80px, 1fr)",
                    gap: 1,
                    width: "100%",
                    maxHeight: "100px",
                    overflowY: "auto",
                  }}
                >
                  {Object.entries(
                    reportData?.formData?.additional_information
                      ?.dailySymptoms || {}
                  ).map(([symptom, checked]) => (
                    <Box
                      key={symptom}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: checked ? "#e6f0fa" : "#f9f9f9",
                        border: checked
                          ? "1px solid #007bff"
                          : "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 1,
                        maxWidth: "120px",
                        minWidth: "70px",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        height: "32px",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontFamily: useFont,
                          color: "#000",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: 1.1,
                        }}
                      >
                        {getSymptomTranslation(symptom)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Box 4 — SIBO test type (R3 C2) */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  height: "auto",
                  minHeight: "60px",
                  gridColumn: "2",
                  gridRow: "3",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: innerTextSize,
                      fontWeight: "bold",
                      fontFamily: useFont,
                      color: "#697e93",
                    }}
                  >
                    {t.siboBreathTest}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: innerTextSize,
                      fontFamily: useFont,
                      textAlign: "right",
                    }}
                  >
                    {reportData?.formData?.additional_information?.InitialTest
                      ?.InitialFindings
                      ? t.initialTest
                      : reportData?.formData?.additional_information
                            ?.InitialTest?.followup_test
                        ? t.followupTest
                        : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography
              sx={{
                mt: 0.5,
                mb: 0.5,
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#697e93",
                fontFamily: useFont,
              }}
            >
              {t.overview}
            </Typography>

            {/* Chart */}
            <Box
              sx={{
                backgroundColor: "#ffffff",
                p: 1,
                borderRadius: 1,
                flex: 1,
                minHeight: "280px",
              }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                >
                  <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="time"
                    ticks={timeIntervals}
                    axisLine={{ stroke: "#000", strokeWidth: 1 }}
                    tickLine={{ stroke: "#000" }}
                    tick={{ fontSize: 11, fontFamily: useFont }}
                    label={{
                      value: t.minutesCap,
                      position: "insideBottom",
                      offset: -6,
                      style: { fontFamily: useFont, fontSize: 12 },
                    }}
                  />
                  <YAxis
                    axisLine={{ stroke: "#000", strokeWidth: 1 }}
                    tickLine={false}
                    ticks={yAxisTicks}
                    tick={{ fontSize: 11, fontFamily: useFont }}
                    label={{
                      value: "PPM",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      style: { fontFamily: useFont, fontSize: 12 },
                    }}
                  />
                  {/* H₂ fallback gray dashed line */}
                  <Line
                    isAnimationActive={false}
                    type="linear"
                    dataKey="H2"
                    stroke="#999"
                    strokeDasharray="4 2"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={true}
                    legendType="none"
                  />
                  {/* H₂ main line */}
                  <Line
                    isAnimationActive={false}
                    type="linear"
                    dataKey="H2"
                    stroke="#0058a5"
                    strokeWidth={2}
                    dot
                    connectNulls={false}
                    name="H₂"
                  />
                  {/* CH₄ fallback gray dashed line */}
                  <Line
                    isAnimationActive={false}
                    type="linear"
                    dataKey="CH4"
                    stroke="#999"
                    strokeDasharray="4 2"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={true}
                    legendType="none"
                  />
                  {/* CH₄ main line */}
                  <Line
                    isAnimationActive={false}
                    type="linear"
                    dataKey="CH4"
                    stroke="#40E0D0"
                    strokeWidth={2}
                    dot
                    connectNulls={false}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{
                      fontFamily: useFont,
                      fontSize: "12px",
                      paddingTop: "10px",
                      paddingLeft: "60px",
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>

              {/* Values table */}
              <TableContainer
                component={Paper}
                sx={{ boxShadow: "none", border: "1px solid #ddd" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: 10,
                          fontWeight: "bold",
                          fontFamily: useFont,
                        }}
                      >
                        {t.minutesCap}
                      </TableCell>
                      {chartData.map((p, idx) => (
                        <TableCell
                          key={`header-${idx}`}
                          align="center"
                          sx={{
                            fontSize: 10,
                            fontWeight: "bold",
                            fontFamily: useFont,
                          }}
                        >
                          {p.time}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: 10,
                          fontWeight: "bold",
                          fontFamily: useFont,
                        }}
                      >
                        H₂
                      </TableCell>
                      {chartData.map((p, idx) => (
                        <TableCell
                          key={`h2-${idx}`}
                          align="center"
                          sx={{ fontFamily: useFont, fontSize: 10 }}
                        >
                          {p.H2 === "-" ? "- *" : p.H2}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: 10,
                          fontWeight: "bold",
                          fontFamily: useFont,
                        }}
                      >
                        CH₄
                      </TableCell>
                      {chartData.map((p, idx) => (
                        <TableCell
                          key={`ch4-${idx}`}
                          align="center"
                          sx={{ fontFamily: useFont, fontSize: 10 }}
                        >
                          {p.CH4 === "-" ? "- *" : p.CH4}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: 10,
                          fontWeight: "bold",
                          fontFamily: useFont,
                          color: "#000",
                        }}
                      >
                        CO₂ (in %):
                      </TableCell>
                      {chartData.map((p, idx) => (
                        <TableCell
                          key={`co2-${idx}`}
                          align="center"
                          sx={{ fontFamily: useFont, fontSize: 10 }}
                        >
                          {p.CO2 === "-" ? "- *" : p.CO2}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Typography
              sx={{
                mt: 0.5,
                mb: 0.5,
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#697e93",
                fontFamily: useFont,
              }}
            >
              {t.summary}
            </Typography>

            {/* Gas rise summary table */}
            <Box
              sx={{
                backgroundColor: "#ffffff",
                p: 2,
                borderRadius: 1,
                flex: 1,
                minHeight: "100px",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  border: "1px solid #ccc",
                }}
              >
                {/* Header row */}
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: "bold",
                      color: "#000",
                      fontFamily: useFont,
                    }}
                  >
                    {t.gasRise}
                  </Typography>
                </Box>
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: "bold",
                      color: "#000",
                      fontFamily: useFont,
                    }}
                  >
                    {t.patientResults}
                  </Typography>
                </Box>
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: "bold",
                      color: "#000",
                      fontFamily: useFont,
                    }}
                  >
                    {t.referenceValues}
                  </Typography>
                </Box>

                {/* H₂ row */}
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: "bold",
                      color: "#000",
                      fontFamily: useFont,
                    }}
                  >
                    SIBO H₂
                  </Typography>
                </Box>
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{ color: "#697e93", fontFamily: useFont, fontSize: 10 }}
                  >
                    {getGasRiseLabel(h2Result)}
                  </Typography>
                </Box>
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{ color: "#697e93", fontFamily: useFont, fontSize: 10 }}
                  >
                    {`< ${referenceValues.H2} ppm`}
                  </Typography>
                </Box>

                {/* CH₄ row */}
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: "bold",
                      color: "#000",
                      fontFamily: useFont,
                    }}
                  >
                    IMO CH₄
                  </Typography>
                </Box>
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{ color: "#697e93", fontFamily: useFont, fontSize: 10 }}
                  >
                    {getGasRiseLabel(ch4Result)}
                  </Typography>
                </Box>
                <Box sx={{ borderBottom: "1px solid #ccc", p: 1 }}>
                  <Typography
                    sx={{ color: "#697e93", fontFamily: useFont, fontSize: 10 }}
                  >
                    {`< ${referenceValues.CH4} ppm`}
                  </Typography>
                </Box>

                {/* CO₂ row */}
                <Box sx={{ p: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "#000",
                      fontFamily: useFont,
                      fontSize: 10,
                    }}
                  >
                    CO₂
                  </Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography
                    sx={{ color: "#697e93", fontFamily: useFont, fontSize: 10 }}
                  >
                    {co2Status}
                  </Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography
                    sx={{ color: "#697e93", fontFamily: useFont, fontSize: 10 }}
                  >
                    {t.steadily}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.continuePage2}
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  fontWeight: "bold",
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.importantRemarks}
              </Typography>
            </Box>

            {/* Page 1 footer */}
            <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#777777",
                  fontFamily: useFont,
                  textAlign: "center",
                }}
              >
                {t.footertext}
              </Typography>
            </Box>
          </Box>
        </Box>
        </Box>
        </Box>

        {/* ── PAGE 2 ─────────────────────────────────────────────────────────── */}
        {/* Scale wrapper: page2Ref keeps its fixed 210mm/297mm size (html2canvas
            captures it unscaled below); only this ancestor pair shrinks the
            on-screen preview to fit narrow viewports. */}
        <Box sx={{ width: PAGE_WIDTH_PX * zoom, height: PAGE_HEIGHT_PX * zoom }}>
        <Box
          sx={{
            width: PAGE_WIDTH_PX,
            height: PAGE_HEIGHT_PX,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
          }}
        >
        <Box
          ref={page2Ref}
          className="report-pdf-root"
          sx={{
            width: "210mm",
            height: "297mm",
            backgroundColor: "#f4f6f9",
            background: "#f4f6f9",
            boxShadow: 5,
            p: 4,
            border: "1px solid #e0e0e0",
            fontFamily: useFont,
          }}
        >
          {/* Header: logo only */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", fontFamily: useFont }}
            ></Typography>
            <img
              className="report-pdf-logo"
              src={REPORT_LOGO_SRC}
              alt="Logo"
              crossOrigin="anonymous"
              style={{
                height: `${LOGO_HEIGHT_PX}px`,
                width: `${LOGO_WIDTH_PX}px`,
                display: "block",
              }}
            />
          </Box>

          {/* Important Remarks */}
          <Box
            sx={{
              backgroundColor: "#ffffff",
              px: 2,
              py: 1,
              borderRadius: 1,
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
              }}
            >
              {t.importantRemarks}
            </Typography>
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
              }}
            >
              {t.importantRemarksDescription}
            </Typography>
          </Box>

          {/* Reference values & notes */}
          <Box
            sx={{
              backgroundColor: "#ffffff",
              mt: 1,
              px: 2,
              py: 1,
              borderRadius: 1,
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
              }}
            >
              {t.referenceText}
            </Typography>

            {/* Point 1 */}
            <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                1.
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.point1first + ` ${cleanedSubstrate} ${t.point1second}`}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
                ml: 3.1,
              }}
            >
              {t.point1textfirst +
                ` ${referenceValues.H2} ` +
                t.point1textsecond +
                ` ${timeLimit} ` +
                t.point1textthird +
                ` ${cleanedSubstrate}.`}
            </Typography>

            {/* Point 2 */}
            <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                2.
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.point2first + ` ${cleanedSubstrate} ` + t.point2second}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
                ml: 3.1,
              }}
            >
              {t.point2textfirst}
            </Typography>

            {/* Point 3 */}
            <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                3.
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.point3first}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
                ml: 3.1,
              }}
            >
              {t.point3textfirst}
            </Typography>

            {/* Additional notes */}
            <Typography
              sx={{
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
              }}
            >
              {t.additionalNoteHeading}
            </Typography>
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
              }}
            >
              {t.additionalNoteTextfirst +
                ` ${timeLimit} ` +
                t.additionalNoteTextsecond}
            </Typography>

            <Box sx={{ mt: 1, ml: 2, display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                •
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.additionalNotePoint1}
              </Typography>
            </Box>

            <Box sx={{ mt: 1, ml: 2, display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                •
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.additionalNotePoint2first +
                  ` ${referenceValues.H2} ` +
                  t.additionalNotePoint2second}
              </Typography>
            </Box>

            {/* Missing data note */}
            {getMissingValuesMessage() && (
              <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
                <Typography
                  sx={{
                    ml: 4.75,
                    fontSize: innerTextSize,
                    fontWeight: "bold",
                    color: "#000000",
                    fontFamily: useFont,
                  }}
                >
                  *
                </Typography>
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    color: "#000000",
                    fontFamily: useFont,
                  }}
                >
                  {getMissingValuesMessage()}
                </Typography>
              </Box>
            )}

            {/* SIBO */}
            <Box
              sx={{
                mt: getMissingValuesMessage() ? 0 : 1,
                display: "flex",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  ml: 0.5,
                  fontSize: innerTextSize,
                  fontWeight: "bold",
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                **SIBO:
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.SIBO}
              </Typography>
            </Box>

            {/* IMO */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  fontWeight: "bold",
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                ***IMO:
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.IMO}
              </Typography>
            </Box>

            {/* Reference footnote */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  ml: 2.4,
                  fontSize: innerTextSize,
                  fontWeight: "bold",
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                ****
              </Typography>
              <Typography
                sx={{
                  fontSize: innerTextSize,
                  color: "#000000",
                  fontFamily: useFont,
                }}
              >
                {t.threestars}
              </Typography>
            </Box>

            {/* Explanation heading */}
            <Typography
              sx={{
                fontSize: innerHeadingSize,
                fontWeight: "bold",
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
                mt: 2,
              }}
            >
              {t.explanation}
            </Typography>

            {/* Explanation body */}
            <Typography
              sx={{
                fontSize: innerTextSize,
                color: "#000000",
                fontFamily: useFont,
                mb: 1,
              }}
            >
              {t.explanationfirst +
                ` ${getExplanationNumericValue(h2Result)} ` +
                t.explanationsecond +
                ` ${timeLimit} ` +
                t.explanationthird +
                `${h2Result.peakValue ?? 0} ` +
                t.explanationfourth +
                ` ${h2Result.peakTime ?? 0} ` +
                t.explanationfifth +
                ` ${h2Result.leftMinValue ?? 0} ` +
                t.explanationsixth}
            </Typography>

            {/* Missing values warning */}
            {hasMissingValues() && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography
                  sx={{
                    fontSize: innerTextSize,
                    color: "#000000",
                    fontFamily: useFont,
                    borderRadius: 1,
                  }}
                >
                  {t.missingValuesWarning}
                </Typography>
              </Box>
            )}
          </Box>
          {/* Footer for Page 2 */}
        </Box>
        </Box>
        </Box>

        {/* Mobile-only zoom controls */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            position: "sticky",
            bottom: 16,
            alignSelf: "center",
            gap: 1,
            bgcolor: "#1a1a1a",
            borderRadius: "999px",
            px: 1.5,
            py: 0.75,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            zIndex: 10,
          }}
        >
          <IconButton size="small" onClick={handleZoomOut} sx={{ color: "#fff" }} aria-label="Zoom out">
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Box
            component="button"
            type="button"
            onClick={handleZoomReset}
            sx={{
              all: "unset",
              cursor: "pointer",
              color: "#fff",
              fontSize: "0.8125rem",
              fontWeight: 600,
              px: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            {Math.round(zoom * 100)}%
          </Box>
          <IconButton size="small" onClick={handleZoomIn} sx={{ color: "#fff" }} aria-label="Zoom in">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }
);

ReportContent.displayName = "ReportContent";

export default ReportContent;
